import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  SET_RESPONSIBILITY_LIST,
  SET_SELECTED_RESPONSIBILITY,
} from "../../store/details";
import {
  LdsButton,
  LdsModal,
  LdsTable,
  LdsPagination,
  LdsSearch,
  LdsCheckbox,
  LdsLoadingSpinner,
} from "@elilillyco/ux-lds-react";
import "./../../assets/scss/details/addRespModal.scss";
import SortableHeader from "../shared/SortableHeader";

const AddMultiRespModal = ({
  isRespModalOpen,
  setIsRespModalOpen,
  updateMultiSelectedResponsibility,
  selectedValues,
  respmode,
  visibleSpecialties,
}) => {
  const { responsibilityList, loading, selectedResponsibilties } = useSelector(
    ({ details }) => details
  );
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  let [data, setData] = useState(responsibilityList);
  let selectedResp = JSON.parse(JSON.stringify(selectedResponsibilties));

  const itemsPerPage = 10;
  const totalPg = Math.ceil(data.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  useEffect(() => {
    let tempData = JSON.parse(JSON.stringify(responsibilityList));
    
    // If respmode is "remove", filter to show only responsibility codes that exist in selected specialties
    if (respmode === "remove" && visibleSpecialties && selectedValues && selectedValues.length > 0) {
      // Collect all unique responsibility codes from selected specialties
      const selectedRespCodes = new Set();
      const allSelectedRCCodes = []; // Store original case codes
      
      for (let i = 0; i < selectedValues.length; i++) {
        const specialty = visibleSpecialties.specialties[selectedValues[i]];
        if (specialty && specialty.responsibility_code) {
          const respCodes = specialty.responsibility_code.split(",").filter(code => code.trim() !== "");
          respCodes.forEach(code => {
            const trimmedCode = code.trim();
            selectedRespCodes.add(trimmedCode.toUpperCase());
            allSelectedRCCodes.push(trimmedCode);
          });
        }
      }
      
      // Check for inactive RC codes (codes in specialties but not in responsibilityList)
      const existingRCCodes = new Set(tempData.map(rc => rc.salesforce_code.trim().toUpperCase()));
      const inactiveRCCodes = [];
      
      allSelectedRCCodes.forEach(code => {
        if (!existingRCCodes.has(code.toUpperCase())) {
          // Found an inactive code - add it if not already added
          if (!inactiveRCCodes.some(ic => ic.toUpperCase() === code.toUpperCase())) {
            inactiveRCCodes.push(code);
          }
        }
      });
      
      // Add inactive RC codes to the list
      if (inactiveRCCodes.length > 0) {
        inactiveRCCodes.forEach(inactiveCode => {
          tempData.push({
            uuid: `inactive_${inactiveCode.toUpperCase()}`,
            salesforce_code: inactiveCode,
            salesforce_description: 'Inactive Responsibility Code',
            active: false,
            isRespSelected: false
          });
        });
      }
      
      // Filter responsibilityList to only include codes that exist in selected specialties
      tempData = tempData.filter(obj => selectedRespCodes.has(obj.salesforce_code.toUpperCase()));
    }
    
    // Apply search filter - only use appliedSearchTerm (triggered by search button/enter)
    if (appliedSearchTerm !== "") {
      // Support comma-separated search terms
      const searchTerms = appliedSearchTerm.split(',').map(term => term.trim().toLowerCase()).filter(term => term !== '');
      
      if (searchTerms.length > 0) {
        tempData = tempData.filter((obj) => {
          const code = obj.salesforce_code.toLowerCase();
          const description = obj.salesforce_description.toLowerCase();
          
          // Check if any of the search terms match
          return searchTerms.some(term => 
            code.includes(term) || description.includes(term)
          );
        });
      }
    }

    // Sort alphabetically by salesforce_code for better readability
    tempData.sort((a, b) => {
      // If no search is applied, show selected items at the top
      if (appliedSearchTerm.trim() === "") {
        if (a.isRespSelected && !b.isRespSelected) return -1;
        if (!a.isRespSelected && b.isRespSelected) return 1;
      }
      
      const codeA = a.salesforce_code.toUpperCase();
      const codeB = b.salesforce_code.toUpperCase();
      if (codeA < codeB) return -1;
      if (codeA > codeB) return 1;
      return 0;
    });

    setData(tempData);
  }, [responsibilityList, appliedSearchTerm, respmode, visibleSpecialties, selectedValues]);

  const tableHead = ["Responsibility Code", "Description"];
  //below function is triggerd when close button is clicked
  function closeModal() {
    setSearchTerm("");
    setAppliedSearchTerm("");
    setIsRespModalOpen(false);
  }
  //below function is triggered when page no. is clicked
  function clickLink(e, value) {
    setCurrentPage(value);
  }
  //below function is triggerd when Next button is clicked in pagination component
  function clickNext() {
    setCurrentPage((prev) => {
      return prev + 1;
    });
  }
  //below function is triggerd when Prev button is clicked in pagination component
  function clickPrev() {
    setCurrentPage((prev) => {
      return prev - 1;
    });
  }
  //below function is triggerd when we submit the value entered in searchbar
  function submitSearch() {
    // Update the applied search term which triggers the useEffect to filter
    setAppliedSearchTerm(searchTerm);
    setCurrentPage(1);
  }
  // below function is triggered when checkbox is clicked
  function checkBoxClicked(event, UUID) {
    // Use responsibilityList to update global state, but data for UI rendering
    let tempResponsibilityList = JSON.parse(JSON.stringify(responsibilityList));
    let tempData = JSON.parse(JSON.stringify(data));
    let updatedSelected = [...selectedResp];

    // Update the checkbox state in both lists
    for (let i = 0; i < tempData.length; i++) {
      if (UUID === tempData[i].uuid) {
        if (event.target.checked === true) {
          tempData[i].isRespSelected = true;
          if (!updatedSelected.includes(UUID)) {
            updatedSelected.push(UUID);
          }
        } else {
          tempData[i].isRespSelected = false;
          updatedSelected = updatedSelected.filter((itm) => itm !== UUID);
        }
      }
    }
    
    // Sync with responsibilityList for global state management
    for (let i = 0; i < tempResponsibilityList.length; i++) {
      if (UUID === tempResponsibilityList[i].uuid) {
        tempResponsibilityList[i].isRespSelected = event.target.checked;
      }
    }

    // Update both the local data state and the global responsibility list
    setData(tempData);
    dispatch(SET_RESPONSIBILITY_LIST(tempResponsibilityList));
    dispatch(SET_SELECTED_RESPONSIBILITY(updatedSelected));
  }
  
  // Select all responsibility codes in the current filtered data
  function selectAllResponsibilities() {
    // Use responsibilityList to update global state, but data for UI rendering
    let tempResponsibilityList = JSON.parse(JSON.stringify(responsibilityList));
    let tempData = JSON.parse(JSON.stringify(data));
    let updatedSelected = [...selectedResp];
    
    // Get all UUIDs from the filtered data
    const filteredUUIDs = tempData.map(item => item.uuid);
    
    for (let i = 0; i < tempData.length; i++) {
      tempData[i].isRespSelected = true;
      if (!updatedSelected.includes(tempData[i].uuid)) {
        updatedSelected.push(tempData[i].uuid);
      }
    }
    
    // Sync with responsibilityList for global state management
    for (let i = 0; i < tempResponsibilityList.length; i++) {
      if (filteredUUIDs.includes(tempResponsibilityList[i].uuid)) {
        tempResponsibilityList[i].isRespSelected = true;
      }
    }

    // Update both the local data state and the global responsibility list
    setData(tempData);
    dispatch(SET_RESPONSIBILITY_LIST(tempResponsibilityList));
    dispatch(SET_SELECTED_RESPONSIBILITY(updatedSelected));
  }
  
  // Unselect all responsibility codes in the current filtered data
  function unselectAllResponsibilities() {
    // Use responsibilityList to update global state, but data for UI rendering
    let tempResponsibilityList = JSON.parse(JSON.stringify(responsibilityList));
    let tempData = JSON.parse(JSON.stringify(data));
    let updatedSelected = [...selectedResp];
    
    // Get all UUIDs from the filtered data
    const filteredUUIDs = tempData.map(item => item.uuid);
    
    for (let i = 0; i < tempData.length; i++) {
      tempData[i].isRespSelected = false;
      updatedSelected = updatedSelected.filter(uuid => uuid !== tempData[i].uuid);
    }
    
    // Sync with responsibilityList for global state management
    for (let i = 0; i < tempResponsibilityList.length; i++) {
      if (filteredUUIDs.includes(tempResponsibilityList[i].uuid)) {
        tempResponsibilityList[i].isRespSelected = false;
      }
    }

    // Update both the local data state and the global responsibility list
    setData(tempData);
    dispatch(SET_RESPONSIBILITY_LIST(tempResponsibilityList));
    dispatch(SET_SELECTED_RESPONSIBILITY(updatedSelected));
  }
  
  // Toggle between select all and unselect all
  function toggleSelectAll() {
    // Check if all items in current filtered data are selected
    const filteredUUIDs = data.map(item => item.uuid);
    const allSelected = filteredUUIDs.every(uuid => selectedResponsibilties.includes(uuid));
    
    if (allSelected) {
      unselectAllResponsibilities();
    } else {
      selectAllResponsibilities();
    }
  }
  
  // Determine button text based on selection state
  function getSelectButtonText() {
    const filteredUUIDs = data.map(item => item.uuid);
    const allSelected = filteredUUIDs.every(uuid => selectedResponsibilties.includes(uuid));
    return allSelected ? "Unselect All" : "Select All";
  }
  const handleAddForm = () => {
    let selectedRespCode = [];
    let str = "";
    // Use the local data state which includes inactive codes
    const filterData = JSON.parse(JSON.stringify(data));
    for (let i = 0; i < filterData.length; i++) {
      if (selectedResp.includes(filterData[i].uuid)) {
        selectedRespCode.push(filterData[i].salesforce_code);
      }
    }
    if (selectedRespCode && selectedRespCode.length > 0) {
      str = selectedRespCode.join(",");
    }
    updateMultiSelectedResponsibility(str);
    setIsRespModalOpen(false);
    dispatch(SET_SELECTED_RESPONSIBILITY([]));
    // dispatch(FETCH_RESPONSIBILITY_LIST());
  };

  useEffect(() => {
    if (!isRespModalOpen) {
      if (responsibilityList && Array.isArray(responsibilityList)) {
        let tempData = JSON.parse(JSON.stringify(responsibilityList));

        for (let i = 0; i < tempData.length; i++) {
          tempData[i].isRespSelected = false;
        }

        setData(tempData);
        dispatch(SET_RESPONSIBILITY_LIST(tempData));
        dispatch(SET_SELECTED_RESPONSIBILITY([]));
        setCurrentPage(1);
        setSearchTerm("");
        setAppliedSearchTerm("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRespModalOpen]);

  return (
    <>
      <LdsModal
        modalId="add-resp-modal"
        open={isRespModalOpen}
        setModalOpen={setIsRespModalOpen}
        heading={`${respmode === "add" ? "Add" : "Remove"} Responsibility`}
      >
        <div>
          <div className="row search-row">
            <div className="col-12 col-md-6">
              <strong>Search Responsibility </strong>
              <LdsSearch
                id={"searchResponsibility"}
                onChange={setSearchTerm}
                value={searchTerm}
                onSubmit={submitSearch}
              />
            </div>
            <div className="col-6 button-col">
              {data.length > 0 && (
                <LdsButton
                  iconPosition="before"
                  classes="button-icon-left blue-bdr-btn"
                  onClick={toggleSelectAll}
                >
                  {getSelectButtonText()}
                </LdsButton>
              )}
            </div>
          </div>
          {loading ? (
            <LdsLoadingSpinner size={90} />
          ) : (
            <div>
              {data.length > 0 ? (
                <>
                  <LdsTable id="specialities-table" flat>
                    <thead>
                      <tr>
                        <SortableHeader
                          headAlign={"align-left b"}
                          header={tableHead}
                          rowData={data}
                          rowKeyName={[
                            "salesforce_code",
                            "salesforce_description",
                          ]}
                          sortedRowData={setData}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {data.map(
                        (itm, index) =>
                          index >= indexOfFirstItem &&
                          index < indexOfLastItem && (
                            <tr key={itm.uuid} style={itm.active === false ? { backgroundColor: '#f8d7da', borderLeft: '4px solid #dc3545' } : {}}>
                              <td>
                                <LdsCheckbox
                                  required={false}
                                  value={itm.uuid}
                                  name={"Responsibility-checkbox-" + itm.uuid}
                                  id={"Responsibility-checkbox-" + itm.uuid}
                                  onChange={(event) =>
                                    checkBoxClicked(event, itm.uuid)
                                  }
                                  checked={itm.isRespSelected}
                                ></LdsCheckbox>
                                <span style={itm.active === false ? { color: '#dc3545', fontWeight: 'bold' } : {}}>
                                  {itm.salesforce_code}
                                </span>
                                {itm.active === false && (
                                  <span style={{ 
                                    color: '#dc3545', 
                                    fontSize: '0.85em', 
                                    marginLeft: '8px',
                                    fontWeight: 'bold',
                                    backgroundColor: '#fff',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    border: '1px solid #dc3545'
                                  }}>
                                    INACTIVE
                                  </span>
                                )}
                              </td>
                              <td>
                                <span style={itm.active === false ? { color: '#dc3545', fontWeight: 'bold' } : {}}>
                                  {itm.salesforce_description}
                                </span>
                                {itm.active === false && (
                                  <div style={{ 
                                    color: '#721c24', 
                                    fontSize: '0.9em', 
                                    fontWeight: '600',
                                    marginTop: '6px',
                                    padding: '4px 8px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #dc3545',
                                    borderRadius: '4px'
                                  }}>
                                    ⚠️ This RC code has been deactivated. Please remove it by checking the box.
                                  </div>
                                )}
                              </td>
                            </tr>
                          )
                      )}
                    </tbody>
                  </LdsTable>
                  <div className="row">
                    <div className="col">
                      <LdsPagination
                        className="floatRight"
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        onLinkClick={clickLink}
                        totalPages={totalPg}
                        onNextClick={clickNext}
                        onPrevClick={clickPrev}
                      />
                    </div>
                  </div>
                  <div className="floatRight">
                    <LdsButton
                      iconPosition="before"
                      classes="button-icon-left blue-bdr-btn"
                      onClick={closeModal}
                    >
                      Cancel
                    </LdsButton>
                    <span className="buttonMargin">
                      <LdsButton
                        iconPosition="before"
                        classes="button-icon-left blue-btn"
                        onClick={handleAddForm}
                      >
                        {`${respmode === "add" ? "Add" : "Remove"}`}
                      </LdsButton>
                    </span>
                  </div>
                </>
              ) : (
                <p className="no-record-message">No Record Found</p>
              )}
            </div>
          )}
        </div>
      </LdsModal>
    </>
  );
};
export default AddMultiRespModal;
